import React from "react";
import { isExpenseLinked, isRentflowPushed } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Eye, CreditCard, RotateCcw, Building2, CheckCheck, Send, AlertTriangle } from "lucide-react";

/** "Logged to Expenses" badge + optional re-push button (paid rows only). */
const LoggedExpenseControls = ({ row, isWriter, expenseLoading, onRequestPush }) => (
  <>
    <Badge
      variant="outline"
      className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700"
      data-testid="payments-expense-logged-badge"
      title={row.expense_post?.expense_id ? `Expense ${row.expense_post.expense_id}` : "Logged to Expenses"}
    >
      <CheckCheck className="h-3 w-3" /> Logged
    </Badge>
    {isWriter && (
      <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground" data-testid="payments-repush-expense-button" disabled={expenseLoading} onClick={() => onRequestPush(row)} title="Push to Expenses again">
        <Building2 className="h-3.5 w-3.5" /> Push again
      </Button>
    )}
  </>
);

/** Expense controls for a paid row: either "logged" state or a first-time push button. */
const PaidExpenseControls = ({ row, isWriter, expenseLoading, onRequestPush }) => {
  if (isExpenseLinked(row.expense_post)) {
    return <LoggedExpenseControls row={row} isWriter={isWriter} expenseLoading={expenseLoading} onRequestPush={onRequestPush} />;
  }
  if (isWriter) {
    return (
      <Button size="sm" variant="outline" className="gap-1" data-testid="payments-push-expense-button" disabled={expenseLoading} onClick={() => onRequestPush(row)} title="Push to Expenses app">
        <Building2 className="h-3.5 w-3.5" /> Push to Expenses
      </Button>
    );
  }
  return null;
};

/** RentFlow push control: status badge (pushed/failed) + push/retry button. */
const RentflowControls = ({ row, isWriter, rentflowPushEnabled, rentflowPushingId, onPushRentflow }) => {
  if (!rentflowPushEnabled) return null;
  const push = row.rentflow_push;
  const pushed = isRentflowPushed(push);
  const failed = push && String(push.status || "").toLowerCase() === "error";
  const busy = rentflowPushingId === row.id;
  return (
    <>
      {pushed && (
        <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="payments-rentflow-pushed-badge" title="Pushed to RentFlow">
          <CheckCheck className="h-3 w-3" /> RentFlow
        </Badge>
      )}
      {failed && (
        <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-800" data-testid="payments-rentflow-failed-badge" title={push.error || "RentFlow push failed"}>
          <AlertTriangle className="h-3 w-3" /> RentFlow failed
        </Badge>
      )}
      {isWriter && (row.rental_matched || failed || pushed) && (
        <Button
          size="sm"
          variant="ghost"
          className="gap-1 text-muted-foreground"
          data-testid="payments-push-rentflow-button"
          disabled={busy}
          onClick={() => onPushRentflow(row)}
          title={pushed ? "Re-push to RentFlow" : failed ? "Retry RentFlow push" : "Push to RentFlow"}
        >
          <Send className={`h-3.5 w-3.5 ${busy ? "animate-pulse" : ""}`} /> {pushed ? "Re-push" : failed ? "Retry" : "RentFlow"}
        </Button>
      )}
    </>
  );
};

/** Row-level action buttons for the payments table. */
export const PaymentActionsCell = ({ row, tabKey, isWriter, expenseLoading, onView, onMarkPaid, onUndo, onRequestPush, rentflowPushEnabled, rentflowPushingId, onPushRentflow }) => (
  <div className="flex items-center justify-end gap-1">
    <Button size="icon" variant="ghost" data-testid="payments-row-view" onClick={onView} title="View bill">
      <Eye className="h-4 w-4" />
    </Button>
    <RentflowControls row={row} isWriter={isWriter} rentflowPushEnabled={rentflowPushEnabled} rentflowPushingId={rentflowPushingId} onPushRentflow={onPushRentflow} />
    {tabKey === "paid" ? (
      <>
        <PaidExpenseControls row={row} isWriter={isWriter} expenseLoading={expenseLoading} onRequestPush={onRequestPush} />
        {isWriter && (
          <Button size="sm" variant="ghost" className="gap-1" data-testid="payments-unpay-button" onClick={() => onUndo(row)} title="Mark unpaid">
            <RotateCcw className="h-3.5 w-3.5" /> Undo
          </Button>
        )}
      </>
    ) : (
      isWriter && (
        <Button size="sm" variant="outline" className="gap-1" data-testid="payments-markpaid-button" onClick={() => onMarkPaid(row)}>
          <CreditCard className="h-3.5 w-3.5" /> Mark paid
        </Button>
      )
    )}
  </div>
);
